/**
 * Database sprint type definitions for Redmine Agile plugin.
 *
 * Maps to the agile_sprints table schema:
 * - id, project_id, name, status (0=open, 1=active, 2=closed)
 * - start_date, end_date, sharing, created_at, updated_at
 */

/**
 * Database status values for sprints.
 * Maps to UI status names.
 */
export const DbSprintStatus = {
  OPEN: 0,     // UI: 'planned'
  ACTIVE: 1,   // UI: 'active'
  CLOSED: 2,   // UI: 'completed'
} as const;

export type DbSprintStatusValue = typeof DbSprintStatus[keyof typeof DbSprintStatus];

/**
 * Database sharing values for cross-project sprint visibility.
 */
export const DbSprintSharing = {
  NONE: 0,        // Project only
  DESCENDANTS: 1, // Subprojects can see
  HIERARCHY: 2,   // Parent/child projects can see
  ALL: 3,         // System-wide visibility
} as const;

export type DbSprintSharingValue = typeof DbSprintSharing[keyof typeof DbSprintSharing];

/**
 * Raw database row type from SELECT queries.
 * Field names match database column names exactly.
 */
export interface DbSprintRow {
  id: number;
  project_id: number;
  name: string;
  status: number;
  start_date: Date | null;
  end_date: Date | null;
  sharing: number;
  created_at: Date;
  updated_at: Date;
}

/**
 * Transformed sprint type for API responses.
 * Uses camelCase and ISO string dates for JSON serialization.
 */
export interface DbSprint {
  id: number;
  projectId: number;
  projectName?: string;         // Added for sprint listing with project info
  projectIdentifier?: string;   // Added for sprint listing with project info
  name: string;
  status: number; // 0=open, 1=active, 2=closed
  startDate: string | null;
  endDate: string | null;
  sharing: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Input type for creating a new sprint.
 */
export interface CreateDbSprintInput {
  name: string;
  status?: number;         // Default: 0 (open)
  startDate?: Date | null;
  endDate?: Date | null;
  sharing?: number;        // Default: 0 (project only)
}

/**
 * Input type for updating a sprint.
 * All fields optional - only provided fields are updated.
 */
export interface UpdateDbSprintInput {
  name?: string;
  status?: number;
  startDate?: Date | null;
  endDate?: Date | null;
  sharing?: number;
}

/**
 * Convert database status number to string representation.
 */
export function statusToString(status: number): 'open' | 'active' | 'closed' {
  switch (status) {
    case DbSprintStatus.OPEN:
      return 'open';
    case DbSprintStatus.ACTIVE:
      return 'active';
    case DbSprintStatus.CLOSED:
      return 'closed';
    default:
      return 'open'; // Default fallback
  }
}

/**
 * Convert string status to database number.
 */
export function statusToNumber(status: string): number {
  switch (status) {
    case 'open':
    case 'planned': // Support legacy UI status name
      return DbSprintStatus.OPEN;
    case 'active':
      return DbSprintStatus.ACTIVE;
    case 'closed':
    case 'completed': // Support legacy UI status name
      return DbSprintStatus.CLOSED;
    default:
      return DbSprintStatus.OPEN; // Default fallback
  }
}

/**
 * Convert a database row to API-friendly DbSprint type.
 * Transforms snake_case to camelCase and Date to ISO string.
 */
export function mapRowToSprint(row: DbSprintRow): DbSprint {
  return {
    id: row.id,
    projectId: row.project_id,
    name: row.name,
    status: row.status,
    startDate: row.start_date ? row.start_date.toISOString() : null,
    endDate: row.end_date ? row.end_date.toISOString() : null,
    sharing: row.sharing,
    createdAt: row.created_at.toISOString(),
    updatedAt: row.updated_at.toISOString(),
  };
}
